import { useEffect, useState } from "react";
import { Reveal, SectionTag } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import api from "@/api";
import Seo, { absoluteUrl, breadcrumbSchema, webPageSchema } from "@/components/Seo";

const MenuPage = () => {
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/menu")
      .then((r) => setMenu(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="menu-page">
      <Seo
        title="Chai Times Menu | Kulhad Chai, Coffee & Snacks in Bengaluru"
        description="Explore the Chai Times menu: kulhad masala chai, kesar chai, coffee, hot milk, vada pav, sandwiches, Maggi, desserts and more across Bengaluru outlets."
        path="/menu"
        image="/kulhad-spices.webp"
        schema={[
          webPageSchema({
            type: "CollectionPage",
            name: "Chai Times Menu",
            description: "Kulhad chai, coffee, hot milk, snacks and café favourites served across Chai Times Bengaluru outlets.",
            path: "/menu",
          }),
          {
            "@type": "Menu",
            "@id": absoluteUrl("/menu#menu"),
            name: "Chai Times Menu",
            hasMenuSection: menu.map((group) => ({
              "@type": "MenuSection",
              name: group.category,
              hasMenuItem: group.items.map((item) => ({
                "@type": "MenuItem",
                name: item.name,
                description: item.description || undefined,
              })),
            })),
          },
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Menu", path: "/menu" }]),
        ]}
      />
      <section className="bg-forest text-cream pt-36 md:pt-44 pb-16 relative grain">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Reveal>
            <SectionTag dark>The Chai Times Menu</SectionTag>
            <h1 className="font-serif font-black text-5xl md:text-7xl tracking-tight mt-5 leading-[0.98]">
              Brewed kadak,<br /><span className="italic text-gold">priced honestly.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-cream/70 leading-relaxed">
              Explore kulhad masala chai, kesar chai, coffee, hot milk, vada pav, sandwiches, Maggi, desserts and café favourites at Chai Times Bengaluru.
            </p>
          </Reveal>
        </div>
      </section>

      <EditorialMarquee text="Served in hand-thrown Kulhads" className="bg-charcoal text-cream" />

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
        {loading ? (
          <p className="font-mono text-sm text-charcoal/50" data-testid="menu-loading">Brewing the menu…</p>
        ) : (
          <>
            <div className="flex flex-nowrap gap-3 mb-16 sticky top-16 md:top-20 z-30 bg-cream/90 backdrop-blur-md py-3 -mx-5 px-5 md:mx-0 md:px-2 overflow-x-auto no-scrollbar" data-testid="menu-category-nav">
              {menu.map((g) => (
                <a
                  key={g.category}
                  href={`#cat-${g.category.replace(/\s/g, "-")}`}
                  data-testid={`menu-cat-${g.category.toLowerCase().replace(/\s/g, "-")}`}
                  className="shrink-0 rounded-full px-5 py-2 font-mono text-[11px] uppercase tracking-[0.15em] border border-clayline hover:bg-terracotta hover:text-cream hover:border-terracotta transition-colors whitespace-nowrap"
                >
                  {g.category}
                </a>
              ))}
            </div>
            <div className="space-y-20">
              {menu.map((g, gi) => (
                <div key={g.category} id={`cat-${g.category.replace(/\s/g, "-")}`} className="scroll-mt-40" data-testid={`menu-section-${g.category.toLowerCase().replace(/\s/g, "-")}`}>
                  <Reveal>
                    <div className="flex items-baseline gap-5 mb-8">
                      <span className="font-serif font-black text-5xl md:text-6xl text-clayline">{String(gi + 1).padStart(2, "0")}</span>
                      <h2 className="font-serif font-bold text-3xl md:text-4xl italic">{g.category}</h2>
                      <span className="flex-1 dot-leader" />
                    </div>
                  </Reveal>
                  <div className="grid md:grid-cols-2 gap-x-14">
                    {g.items.map((item, i) => (
                      <Reveal key={item.id} delay={Math.min(i * 0.04, 0.3)}>
                        <div className="flex items-baseline gap-4 py-4 border-b border-clayline/50 group" data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                          {item.image && (
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${item.image}`}
                              alt={`${item.name} at Chai Times Bengaluru`}
                              loading="lazy"
                              className="w-14 h-14 rounded-xl object-cover self-center shrink-0 border border-clayline"
                            />
                          )}
                          <div className="min-w-0">
                            <h3 className="font-sans font-bold text-lg group-hover:text-terracotta transition-colors">{item.name}</h3>
                            {item.description && <p className="text-sm text-charcoal/55 mt-0.5">{item.description}</p>}
                          </div>
                          <span className="flex-1 dot-leader" />
                          {item.sizes ? (
                            <span className="font-sans font-bold text-terracotta whitespace-nowrap text-xs sm:text-base tracking-wide">
                              S ₹{item.sizes.S} · M ₹{item.sizes.M} · L ₹{item.sizes.L}
                            </span>
                          ) : item.price != null ? (
                            <span className="font-sans font-bold text-terracotta whitespace-nowrap text-base tracking-wide">₹{item.price}</span>
                          ) : (
                            <span className="font-mono text-[11px] uppercase tracking-wider text-charcoal/45 whitespace-nowrap">At counter</span>
                          )}
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default MenuPage;
